import { Language } from './translations';

export type LegalPageKey = 'privacy' | 'security' | 'terms';

export type LegalSection = {
  title: string;
  paragraphs: string[];
};

export type LegalPageContent = {
  title: string;
  lastUpdated: string;
  intro: string;
  sections: LegalSection[];
};

export const legalContent: Record<Language, Record<LegalPageKey, LegalPageContent>> = {
  en: {
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: July 9, 2026',
      intro:
        'SOLTEC respects your privacy. This policy explains what information we collect when you use soltecsoftware.com and how we use it.',
      sections: [
        {
          title: 'Information we collect',
          paragraphs: [
            'When you contact us through the website form, we collect the details you provide: name, email address, company (optional), budget range (optional), and project message.',
            'We may also collect basic technical data such as browser type, device, and approximate location through standard server and security logs.',
          ],
        },
        {
          title: 'How we use your information',
          paragraphs: [
            'We use contact form submissions to respond to inquiries, scope projects, and communicate about our services.',
            'We do not sell your personal information. We only share data with service providers required to operate the website, such as Formspree (form delivery) and Netlify (hosting).',
          ],
        },
        {
          title: 'Cookies and analytics',
          paragraphs: [
            'The contact form may use Google reCAPTCHA to reduce spam. reCAPTCHA may set cookies and process usage signals according to Google\'s policies.',
            'We may use essential cookies to remember your language and theme preferences in your browser.',
          ],
        },
        {
          title: 'Data retention',
          paragraphs: [
            'We keep contact inquiries only as long as needed to respond, manage projects, or meet legal obligations, then delete or anonymize them when no longer required.',
          ],
        },
        {
          title: 'Your rights',
          paragraphs: [
            'Depending on your location, you may have rights to access, correct, or delete your personal information. To exercise these rights, contact us at soltecsoftware@outlook.com.',
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            'For privacy questions, email soltecsoftware@outlook.com. SOLTEC is based in Quebec, Canada.',
          ],
        },
      ],
    },
    security: {
      title: 'Security',
      lastUpdated: 'Last updated: July 9, 2026',
      intro:
        'We take reasonable measures to protect the SOLTEC website and the information shared with us. This page summarizes our security practices.',
      sections: [
        {
          title: 'Website infrastructure',
          paragraphs: [
            'soltecsoftware.com is hosted on Netlify with HTTPS enabled. Traffic between your browser and our site is encrypted in transit.',
            'We keep dependencies updated and run production builds with standard security checks before deployment.',
          ],
        },
        {
          title: 'Form and communication security',
          paragraphs: [
            'Contact form submissions are processed through Formspree over encrypted connections. Spam protection (Formshield and reCAPTCHA) helps filter automated abuse.',
            'We recommend that you do not send passwords, payment card numbers, or other highly sensitive credentials through the public contact form.',
          ],
        },
        {
          title: 'Access control',
          paragraphs: [
            'Access to form submissions and project communications is limited to authorized SOLTEC team members who need the information to respond and deliver services.',
          ],
        },
        {
          title: 'Incident reporting',
          paragraphs: [
            'If you discover a security issue related to our website or services, please report it promptly to soltecsoftware@outlook.com with enough detail for us to investigate.',
          ],
        },
        {
          title: 'Limitations',
          paragraphs: [
            'No online service can guarantee absolute security. We continuously improve our practices, but you use the website at your own risk as described in our Terms of Service.',
          ],
        },
      ],
    },
    terms: {
      title: 'Terms of Service',
      lastUpdated: 'Last updated: July 9, 2026',
      intro:
        'By accessing soltecsoftware.com, you agree to these Terms of Service. If you do not agree, please do not use the website.',
      sections: [
        {
          title: 'Use of the website',
          paragraphs: [
            'You may browse the site and contact us for legitimate business inquiries. You agree not to misuse the site, attempt unauthorized access, or interfere with its operation.',
          ],
        },
        {
          title: 'Services and proposals',
          paragraphs: [
            'Content on this website describes SOLTEC services in general terms. It does not constitute a binding offer. Project scope, pricing, and deliverables are defined only in a separate written agreement signed by both parties.',
          ],
        },
        {
          title: 'Intellectual property',
          paragraphs: [
            'Unless otherwise stated, the website content, branding, and materials are owned by SOLTEC or its licensors. You may not copy, modify, or redistribute them without prior written permission.',
          ],
        },
        {
          title: 'Disclaimer',
          paragraphs: [
            'The website is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free operation.',
          ],
        },
        {
          title: 'Limitation of liability',
          paragraphs: [
            'To the fullest extent permitted by law, SOLTEC is not liable for indirect, incidental, or consequential damages arising from your use of the website.',
          ],
        },
        {
          title: 'Governing law',
          paragraphs: [
            'These terms are governed by the laws applicable in Quebec, Canada, without regard to conflict-of-law rules. Disputes will be handled by the competent courts in Quebec, unless otherwise required by mandatory local law.',
          ],
        },
        {
          title: 'Contact',
          paragraphs: [
            'Questions about these terms can be sent to soltecsoftware@outlook.com.',
          ],
        },
      ],
    },
  },
  es: {
    privacy: {
      title: 'Política de privacidad',
      lastUpdated: 'Última actualización: 9 de julio de 2026',
      intro:
        'SOLTEC respeta tu privacidad. Esta política explica qué información recopilamos cuando usas soltecsoftware.com y cómo la utilizamos.',
      sections: [
        {
          title: 'Información que recopilamos',
          paragraphs: [
            'Cuando nos contactas mediante el formulario del sitio, recopilamos los datos que proporcionas: nombre, correo electrónico, empresa (opcional), rango de presupuesto (opcional) y mensaje del proyecto.',
            'También podemos recopilar datos técnicos básicos como tipo de navegador, dispositivo y ubicación aproximada a través de registros estándar del servidor y de seguridad.',
          ],
        },
        {
          title: 'Cómo usamos tu información',
          paragraphs: [
            'Usamos los envíos del formulario de contacto para responder consultas, definir proyectos y comunicarnos sobre nuestros servicios.',
            'No vendemos tu información personal. Solo compartimos datos con proveedores necesarios para operar el sitio, como Formspree (entrega de formularios) y Netlify (alojamiento).',
          ],
        },
        {
          title: 'Cookies y analítica',
          paragraphs: [
            'El formulario de contacto puede usar Google reCAPTCHA para reducir spam. reCAPTCHA puede establecer cookies y procesar señales de uso según las políticas de Google.',
            'Podemos usar cookies esenciales para recordar tus preferencias de idioma y tema en tu navegador.',
          ],
        },
        {
          title: 'Conservación de datos',
          paragraphs: [
            'Conservamos las consultas de contacto solo el tiempo necesario para responder, gestionar proyectos o cumplir obligaciones legales, y luego las eliminamos o anonimizamos cuando ya no se requieran.',
          ],
        },
        {
          title: 'Tus derechos',
          paragraphs: [
            'Según tu ubicación, puedes tener derecho a acceder, corregir o eliminar tu información personal. Para ejercer estos derechos, contáctanos en soltecsoftware@outlook.com.',
          ],
        },
        {
          title: 'Contacto',
          paragraphs: [
            'Para preguntas sobre privacidad, escribe a soltecsoftware@outlook.com. SOLTEC tiene su base en Quebec, Canadá.',
          ],
        },
      ],
    },
    security: {
      title: 'Seguridad',
      lastUpdated: 'Última actualización: 9 de julio de 2026',
      intro:
        'Tomamos medidas razonables para proteger el sitio web de SOLTEC y la información que se comparte con nosotros. Esta página resume nuestras prácticas de seguridad.',
      sections: [
        {
          title: 'Infraestructura del sitio',
          paragraphs: [
            'soltecsoftware.com está alojado en Netlify con HTTPS habilitado. El tráfico entre tu navegador y nuestro sitio se cifra en tránsito.',
            'Mantenemos las dependencias actualizadas y ejecutamos compilaciones de producción con controles de seguridad estándar antes del despliegue.',
          ],
        },
        {
          title: 'Seguridad del formulario y comunicaciones',
          paragraphs: [
            'Los envíos del formulario de contacto se procesan a través de Formspree mediante conexiones cifradas. La protección antispam (Formshield y reCAPTCHA) ayuda a filtrar abusos automatizados.',
            'Te recomendamos no enviar contraseñas, números de tarjetas de pago u otros datos altamente sensibles a través del formulario público de contacto.',
          ],
        },
        {
          title: 'Control de acceso',
          paragraphs: [
            'El acceso a los envíos del formulario y a las comunicaciones de proyectos está limitado a los miembros autorizados del equipo SOLTEC que necesitan la información para responder y prestar servicios.',
          ],
        },
        {
          title: 'Reporte de incidentes',
          paragraphs: [
            'Si descubres un problema de seguridad relacionado con nuestro sitio o servicios, repórtalo de inmediato a soltecsoftware@outlook.com con suficiente detalle para que podamos investigarlo.',
          ],
        },
        {
          title: 'Limitaciones',
          paragraphs: [
            'Ningún servicio en línea puede garantizar seguridad absoluta. Mejoramos continuamente nuestras prácticas, pero usas el sitio bajo tu propio riesgo según se describe en nuestros Términos de servicio.',
          ],
        },
      ],
    },
    terms: {
      title: 'Términos de servicio',
      lastUpdated: 'Última actualización: 9 de julio de 2026',
      intro:
        'Al acceder a soltecsoftware.com, aceptas estos Términos de servicio. Si no estás de acuerdo, por favor no uses el sitio web.',
      sections: [
        {
          title: 'Uso del sitio web',
          paragraphs: [
            'Puedes navegar el sitio y contactarnos para consultas comerciales legítimas. Aceptas no hacer un uso indebido del sitio, intentar accesos no autorizados ni interferir con su funcionamiento.',
          ],
        },
        {
          title: 'Servicios y propuestas',
          paragraphs: [
            'El contenido de este sitio describe los servicios de SOLTEC en términos generales. No constituye una oferta vinculante. El alcance del proyecto, los precios y los entregables se definen únicamente en un acuerdo escrito separado firmado por ambas partes.',
          ],
        },
        {
          title: 'Propiedad intelectual',
          paragraphs: [
            'Salvo que se indique lo contrario, el contenido del sitio, la marca y los materiales pertenecen a SOLTEC o a sus licenciantes. No puedes copiarlos, modificarlos ni redistribuirlos sin permiso previo por escrito.',
          ],
        },
        {
          title: 'Descargo de responsabilidad',
          paragraphs: [
            'El sitio web se proporciona "tal cual" sin garantías de ningún tipo. No garantizamos un funcionamiento ininterrumpido o libre de errores.',
          ],
        },
        {
          title: 'Limitación de responsabilidad',
          paragraphs: [
            'En la máxima medida permitida por la ley, SOLTEC no es responsable de daños indirectos, incidentales o consecuentes derivados del uso del sitio web.',
          ],
        },
        {
          title: 'Ley aplicable',
          paragraphs: [
            'Estos términos se rigen por las leyes aplicables en Quebec, Canadá, sin considerar reglas de conflicto de leyes. Las disputas se resolverán ante los tribunales competentes de Quebec, salvo que la ley local obligatoria disponga otra cosa.',
          ],
        },
        {
          title: 'Contacto',
          paragraphs: [
            'Las preguntas sobre estos términos pueden enviarse a soltecsoftware@outlook.com.',
          ],
        },
      ],
    },
  },
};
